import { useQuery } from '@tanstack/react-query';
import {
  getAuditLog,
  getAuditLogFilters,
  listAuditLogs,
  type AuditLogDto,
  type AuditLogFilterOptions,
  type ListAuditLogsParams,
  type PaginatedAuditLogs,
} from '@/lib/audit-logs';

/**
 * Hierarchical query-key factory for the audit-logs domain.
 *
 * Keys are deliberately **workspace-agnostic** (no `workspaceId` segment): the
 * server scopes every read to the caller's org/workspace via the JWT, and the
 * workspace-switch mutation's unfiltered `qc.invalidateQueries()` (per the
 * `apps/web/CLAUDE.md` tenant-switch rule) refetches these under the new
 * workspace. Keying by workspace would leave a stale per-workspace slot live.
 */
export const auditLogKeys = {
  all: ['audit-logs'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown> = {}) =>
    [...auditLogKeys.lists(), filters] as const,
  filters: () => [...auditLogKeys.all, 'filters'] as const,
  detail: (id: number) => [...auditLogKeys.all, 'detail', id] as const,
};

/** Shared list query options — used by the route loader's `ensureQueryData`
 * prime and the page's `useAuditLogsQuery` so both build the identical key. */
export function auditLogsListQueryOptions(params: ListAuditLogsParams) {
  return {
    queryKey: auditLogKeys.list(params),
    queryFn: () => listAuditLogs(params),
  };
}

/**
 * Offset-paginated list query. `placeholderData: (prev) => prev` keeps the
 * previous page visible during a page/filter transition so the table doesn't
 * flash empty. `silentError` because the page owns the inline error surface — a
 * 403 (`190001`) renders a "not authorized" state and every other failure gets
 * a retry button, so the global auto-toast must not double-report.
 */
export function useAuditLogsQuery(params: ListAuditLogsParams) {
  return useQuery<PaginatedAuditLogs>({
    ...auditLogsListQueryOptions(params),
    placeholderData: (prev) => prev,
    meta: { silentError: true },
  });
}

/** Distinct filter values for the drawer dropdowns. `silentError`: a failed
 * fetch just leaves the pickers empty; the list is unaffected. */
export function useAuditLogFiltersQuery() {
  return useQuery<AuditLogFilterOptions>({
    queryKey: auditLogKeys.filters(),
    queryFn: () => getAuditLogFilters(),
    staleTime: 5 * 60_000,
    meta: { silentError: true },
  });
}

/** One entry by id, for the before→after diff dialog. Dormant until an entry is
 * opened (`id != null`). `silentError`: the dialog renders its own retry. */
export function useAuditLogQuery(id: number | null) {
  return useQuery<AuditLogDto>({
    queryKey: auditLogKeys.detail(id ?? 0),
    queryFn: () => getAuditLog(id!),
    enabled: id != null,
    meta: { silentError: true },
  });
}
